import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { AccountProfile, SellerProfile, type SwitchableAccountProfile } from '@enums/accountProfile.ts';
import { SELLER_MANAGEMENT } from '@pages/inbox/conversations-show/ProfileEnum';
import { useAuthStore } from '@stores/useAuthStore';

export const useUserProfile = defineStore('userProfile', () => {
  const currentProfile = ref<SwitchableAccountProfile | null>(null);

  const { user } = useAuthStore();

  const userProfile = computed<AccountProfile>(() => {
    if (!user) {
      return AccountProfile.ANONYMOUS;
    }

    if (!user.organization) {
      throw new Error('Organization not found for user');
    }

    const {
      seller_profiles: sellerProfiles,
      buyer,
    } = user.organization;

    const hasSellerProfiles = sellerProfiles?.length > 0;

    if (!buyer) {
      return hasSellerProfiles ? AccountProfile.SELLER : AccountProfile.UNQUALIFIED;
    }

    return hasSellerProfiles ? AccountProfile.BOTH : AccountProfile.BUYER;
  });

  const isSellerBoost = computed<boolean>(() => {
    if (!user || userProfile.value === AccountProfile.BUYER) {
      return false;
    }

    if (!user.organization) {
      throw new Error('Organization not found for user');
    }

    const { seller_profiles: sellerProfiles } = user.organization;

    return Array.isArray(sellerProfiles)
      && (sellerProfiles.includes(SellerProfile.SELLER_BOOST) || sellerProfiles.includes(SellerProfile.SELLER_BOOST));
  });

  const hasKYCActive = computed<boolean>(() => {
    if (!user?.organization?.pay_organization_seller) {
      return false;
    }

    return user.organization.pay_organization_seller.has_kyc_active;
  });

  const hasPayBuyer = computed<boolean>(() => {
    if (!user?.organization) {
      return false;
    }

    const subscriptions = user.organization.pay_organization_buyer?.subscriptions;

    // Only show buyer payment method if organization is linked to a PayOrganizationBuyer entity and has at least one subscription
    return !!subscriptions && subscriptions.length > 0;
  });

  const isBuyer = computed<boolean>(() => !!user?.organization?.buyer);

  const isProfileManagement = computed<boolean>(() => {
    if (!user || !user.organization) {
      return false;
    }

    return user.organization.seller_profiles.includes(SELLER_MANAGEMENT);
  });

  const persistCurrentProfile = (preference : SwitchableAccountProfile): void => {
    localStorage.setItem('menuPreference', preference);
  };

  const switchCurrentProfile = (): void => {
    if (userProfile.value !== AccountProfile.BOTH) {
      return;
    }

    currentProfile.value = currentProfile.value === AccountProfile.SELLER ? AccountProfile.BUYER : AccountProfile.SELLER;

    persistCurrentProfile(currentProfile.value);
  };

  const initCurrentProfile = (): void => {
    if (userProfile.value === AccountProfile.BUYER) {
      currentProfile.value = AccountProfile.BUYER;
      return;
    }

    if (userProfile.value === AccountProfile.SELLER) {
      currentProfile.value = AccountProfile.SELLER;
      return;
    }

    const menuPreference = localStorage.getItem('menuPreference');

    if (userProfile.value === AccountProfile.BOTH) {
      currentProfile.value = menuPreference ? menuPreference as SwitchableAccountProfile : AccountProfile.SELLER;
      return;
    }

    currentProfile.value = null;
  };

  watch(userProfile, () => initCurrentProfile(), { immediate: true });

  return {
    user,
    userProfile,
    isSellerBoost,
    hasKYCActive,
    hasPayBuyer,
    isBuyer,
    currentProfile,
    switchCurrentProfile,
    isProfileManagement,
  };
});
